import React, { useState } from 'react';
import PageHero from '../components/ui/PageHero';
import { Mail, Phone, MapPin, Send, CheckCircle, Linkedin, Instagram, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import SEO from '../components/SEO';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });
      
      const result = await response.json();
      
      if (response.ok && result.success) {
        setSubmitted(true);
        setTimeout(() => {
          setSubmitted(false);
          setFormData({
            name: '',
            email: '',
            company: '',
            phone: '',
            subject: '',
            message: ''
          });
        }, 5000);
      } else {
        throw new Error(result.detail || 'Erreur lors de l\'envoi');
      }
    } catch (err) {
      console.error('Contact form error:', err);
      setError('Une erreur est survenue. Veuillez réessayer ou nous contacter directement par email.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <SEO
        title="Devis événement entreprise Lyon — Contact"
        description="Contactez Mon Evénemenciel pour organiser vos événements d'entreprise. Devis gratuit, réponse rapide. Email: contact@monevenemenciel.eu - Tél: 06 12 93 84 63"
        keywords="contact Mon Evénemenciel, devis événement entreprise, organiser team building, demande événementiel Lyon"
        url="/contact"
        image="/img/photo-evenement.webp"
      />
      <PageHero
        title="Contact"
        subtitle="Parlons de vos projets !"
        backgroundImage="/img/photo-evenement.webp"
      />

      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Info */}
            <div>
              <h2 className="text-3xl font-display text-brand-petrol mb-4">
                Contactez-nous
              </h2>
              <p className="text-[#96452f] font-display italic text-xl mb-8">
                Nous sommes là pour répondre à toutes vos questions
              </p>
              
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-[#96452f]/10 rounded-md">
                    <Mail className="w-5 h-5 text-[#96452f]" strokeWidth={1.5} />
                  </div>
                  <div>
                    <h4 className="font-semibold text-brand-petrol">Email</h4>
                    <a
                      href="mailto:contact@monevenemenciel.eu"
                      className="text-brand-petrol/70 hover:text-[#96452f] transition-colors"
                    >
                      contact@monevenemenciel.eu
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="p-3 bg-[#D08154]/10 rounded-md">
                    <Phone className="w-5 h-5 text-[#D08154]" strokeWidth={1.5} />
                  </div>
                  <div>
                    <h4 className="font-semibold text-brand-petrol">Téléphone</h4>
                    <a
                      href="tel:+33612938463"
                      className="text-brand-petrol/70 hover:text-[#D08154] transition-colors"
                    >
                      +33 6 12 93 84 63
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="p-3 bg-gray-100 rounded-full">
                    <MapPin className="w-6 h-6 text-brand-petrol/70" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-brand-petrol">Siège social</h4>
                    <p className="text-brand-petrol/70">
                      All. des Prés Rouets B36<br />
                      69510 Messimy
                    </p>
                    <a
                      href="https://www.google.com/maps/search/?api=1&query=All.+des+Prés+Rouets+B36+69510+Messimy"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-[#D08154] hover:underline mt-1 inline-block"
                    >
                      Voir sur Google Maps
                    </a>
                  </div>
                </div>
              </div>

              {/* Social Media */}
              <div className="mt-8">
                <h4 className="font-semibold text-brand-petrol mb-4">Suivez-nous</h4>
                <div className="flex gap-4">
                  <a
                    href="https://www.linkedin.com/company/mon-evenemenciel"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-3 bg-gray-100 text-brand-petrol/80 rounded-full hover:bg-gray-200 transition-colors duration-200 hover:scale-110"
                  >
                    <Linkedin className="w-5 h-5" />
                  </a>
                  <a
                    href="https://www.instagram.com/monevenemenciel/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-3 bg-gray-100 text-brand-petrol/80 rounded-full hover:bg-gray-200 transition-all duration-200 hover:scale-110"
                  >
                    <Instagram className="w-5 h-5" />
                  </a>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="bg-brand-ivory rounded-2xl p-8">
              {submitted ? (
                <div className="flex flex-col items-center justify-center h-full py-12">
                  <CheckCircle className="w-16 h-16 text-green-500 mb-4" />
                  <h3 className="text-2xl font-display text-brand-petrol mb-2">
                    Message envoyé !
                  </h3>
                  <p className="text-brand-petrol/70 text-center">
                    Nous vous répondrons dans les plus brefs délais.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="name">Nom *</Label>
                      <Input
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="bg-white"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email">Email *</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="bg-white"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="company">Entreprise</Label>
                      <Input
                        id="company"
                        name="company"
                        value={formData.company}
                        onChange={handleChange}
                        className="bg-white"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="phone">Téléphone</Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleChange}
                        className="bg-white"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="subject">Sujet *</Label>
                    <Input
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      required
                      className="bg-white"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message">Message *</Label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={5}
                      className="bg-white resize-none"
                    />
                  </div>

                  {error && (
                    <div className="bg-red-50 text-red-600 p-3 rounded-lg text-sm">
                      {error}
                    </div>
                  )}

                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-[#96452f] hover:bg-[#7a3826] text-white py-3 rounded-md font-medium transition-all duration-300 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        Envoi en cours...
                      </>
                    ) : (
                      <>
                        <Send className="w-5 h-5 mr-2" />
                        Envoyer le message
                      </>
                    )}
                  </Button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
